import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Gear, Warning, Sparkle } from "@phosphor-icons/react";
import { MAPPING_FIELD_LABELS, UNMAPPED } from "./constants";

/**
 * MappingDialog — column-mapping UI. Rendered controlled by parent.
 * Parent owns draft state, save/auto-detect actions, and the current preview
 * (whose columns drive the Select options).
 */
export default function MappingDialog({
  open,
  onOpenChange,
  uploadPreview,
  mappingKeys,
  mappingDraft,
  setMappingDraft,
  savingMapping,
  onSave,
  onAutoDetect,
}) {
  const rows = [
    ...(mappingKeys.keys_required || []).map((k) => ({ key: k, required: true })),
    ...(mappingKeys.keys_optional || []).map((k) => ({ key: k, required: false })),
  ];
  const hasColumns = (uploadPreview?.columns || []).length > 0;
  const showAutoDetect = uploadPreview?.suggested_mapping &&
    Object.keys(uploadPreview.suggested_mapping).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gear size={20} weight="bold" /> Mapping Kolom Excel
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="mapping-dialog-body">
          <p className="text-xs text-muted-foreground">
            Cocokkan setiap kunci internal dengan nama kolom di file Excel Anda. Kunci wajib (*) tidak boleh kosong. Perubahan berlaku untuk upload berikutnya.
          </p>
          {hasColumns && (
            <div className="text-xs bg-secondary/40 p-2 border border-border">
              <div className="overline mb-1">Kolom terdeteksi ({uploadPreview.columns.length})</div>
              <div className="font-mono text-[10px] break-words">
                {uploadPreview.columns.join(" · ")}
              </div>
            </div>
          )}
          {rows.map(({ key, required }) => (
            <div key={key} className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label className="overline text-xs">
                {MAPPING_FIELD_LABELS[key] || key}{required && " *"}
              </Label>
              {hasColumns ? (
                <Select
                  value={mappingDraft[key] || UNMAPPED}
                  onValueChange={(v) => setMappingDraft((d) => ({ ...d, [key]: v === UNMAPPED ? "" : v }))}
                >
                  <SelectTrigger className="rounded-none h-10" data-testid={`mapping-select-${key}`}>
                    <SelectValue placeholder="— Pilih kolom —" />
                  </SelectTrigger>
                  <SelectContent>
                    {!required && <SelectItem value={UNMAPPED}>— Tidak dipetakan —</SelectItem>}
                    {uploadPreview.columns.map((col) => (
                      <SelectItem key={col} value={col}>{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <input
                  type="text"
                  value={mappingDraft[key] || ""}
                  onChange={(e) => setMappingDraft((d) => ({ ...d, [key]: e.target.value }))}
                  placeholder={required ? "Nama kolom Excel (wajib)" : "Nama kolom Excel (opsional)"}
                  className="border border-border rounded-none h-10 px-3 text-sm font-mono focus:outline-none focus:border-primary"
                  data-testid={`mapping-input-${key}`}
                />
              )}
            </div>
          ))}
          {!hasColumns && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-300 p-2">
              <Warning size={12} className="inline mr-1" weight="bold" />
              Upload file XLSX dulu untuk pilih dari kolom terdeteksi. Sekarang input manual.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Batal</Button>
          {showAutoDetect && (
            <Button
              variant="secondary"
              className="rounded-none font-bold uppercase tracking-wider"
              onClick={onAutoDetect}
              data-testid="auto-detect-mapping-button"
              title="Isi otomatis dari fuzzy-match kolom Excel"
            >
              <Sparkle size={14} className="mr-1" weight="fill" /> Auto-Detect
            </Button>
          )}
          <Button
            onClick={onSave}
            disabled={savingMapping}
            className="rounded-none font-bold uppercase tracking-wider"
            data-testid="save-mapping-button"
          >
            {savingMapping ? "Menyimpan..." : "Simpan Mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
